import * as XLSX from 'xlsx';
import { 
  DataProductionRecord, 
  ExcelFilters, 
  ExcelProcessingResult
} from '../types/wizard';

/**
 * Normalize factory names (e.g., "ITAPETININGA MDP" -> "ITAPETININGA MDP")
 */
const normalizeFactoryName = (name: string): string => {
  if (!name) return '';
  return name.trim().toUpperCase();
};

/**
 * Normalize numeric values handling thousands separator (.) vs decimal separator (,)
 */
const normalizeNumericValue = (value: any): number => {
  if (typeof value === 'number' && !isNaN(value)) {
    return value;
  }
  
  if (typeof value === 'string') {
    // Remove whitespace
    const cleanValue = value.trim();
    
    // Empty string = 0
    if (cleanValue === '' || cleanValue === '-') {
      return 0;
    }
    
    // If contains comma, treat as decimal separator
    if (cleanValue.includes(',')) {
      // Replace thousands separator (.) and use comma as decimal
      const normalized = cleanValue.replace(/\./g, '').replace(',', '.');
      const parsed = parseFloat(normalized);
      return isNaN(parsed) ? 0 : parsed;
    }
    
    // If contains only dots, treat as thousands separator
    if (cleanValue.includes('.')) {
      // Count dots to determine if it's thousands separator or decimal
      const dotCount = (cleanValue.match(/\./g) || []).length;
      if (dotCount === 1) {
        // Could be thousands (1.710) or decimal (1.5)
        const parts = cleanValue.split('.');
        if (parts[1] && parts[1].length === 3) {
          // Likely thousands separator (1.710 = 1710)
          const normalized = cleanValue.replace('.', '');
          const parsed = parseInt(normalized);
          return isNaN(parsed) ? 0 : parsed;
        } else {
          // Likely decimal separator (1.5 = 1.5)
          const parsed = parseFloat(cleanValue);
          return isNaN(parsed) ? 0 : parsed;
        }
      } else {
        // Multiple dots, treat as thousands separators
        const normalized = cleanValue.replace(/\./g, '');
        const parsed = parseInt(normalized);
        return isNaN(parsed) ? 0 : parsed;
      }
    }
    
    // Try to parse as regular number
    const parsed = parseFloat(cleanValue);
    return isNaN(parsed) ? 0 : parsed;
  }
  
  // Default to 0 for any other type
  return 0;
};

/**
 * Check if a row contains day numbers (1-31) to identify header row
 */
const isHeaderRowWithDays = (row: any[]): boolean => {
  let dayCount = 0;
  for (let i = 0; i < row.length; i++) {
    const cell = row[i];
    if (typeof cell === 'number' && cell >= 1 && cell <= 31) {
      dayCount++;
    }
  }
  return dayCount >= 10; // At least 10 days found
};

/**
 * Check if a row contains a factory name
 */
const isFactoryRow = (row: any[]): boolean => {
  for (let i = 0; i < row.length; i++) {
    const cell = row[i];
    if (typeof cell === 'string') {
      const cellText = cell.trim().toUpperCase();
      // Look for factory patterns like "XXXX MDP" or specific factory names
      if (cellText.includes('MDP') || 
          cellText.includes('ITAPETININGA') || 
          cellText.includes('UBERABA') || 
          cellText.includes('TAQUARI')) {
        return true;
      }
    }
  }
  return false;
};

/**
 * Extract factory name from a factory row
 */
const extractFactoryName = (row: any[]): string => {
  for (let i = 0; i < row.length; i++) {
    const cell = row[i];
    if (typeof cell === 'string') {
      const cellText = cell.trim();
      if (cellText.includes('MDP') || 
          cellText.includes('ITAPETININGA') || 
          cellText.includes('UBERABA') || 
          cellText.includes('TAQUARI')) {
        return normalizeFactoryName(cellText);
      }
    }
  }
  return '';
};

/**
 * Find the production sheet by looking for day columns and factory names
 */
const findProductionSheet = (workbook: XLSX.WorkBook): XLSX.WorkSheet | null => {
  for (const sheetName of workbook.SheetNames) {
    const sheet = workbook.Sheets[sheetName];
    const range = XLSX.utils.decode_range(sheet['!ref'] || 'A1');
    
    // Convert sheet to array of arrays for analysis
    const rows: any[][] = [];
    for (let R = range.s.r; R <= Math.min(range.e.r, range.s.r + 30); R++) {
      const row: any[] = [];
      for (let C = range.s.c; C <= range.e.c; C++) {
        const cellAddress = XLSX.utils.encode_cell({ c: C, r: R });
        const cell = sheet[cellAddress];
        row.push(cell ? cell.v : null);
      }
      rows.push(row);
    }
    
    // Check if this sheet has day columns (1-31)
    const hasHeaderWithDays = rows.some(row => isHeaderRowWithDays(row));
    
    // Check if the sheet contains factory names
    const hasFactoryNames = rows.some(row => isFactoryRow(row));
    
    if (hasHeaderWithDays && hasFactoryNames) {
      return sheet;
    }
  }
  
  return null;
};

/**
 * Parse production data from Excel following the specific structure:
 * - Factories in column B
 * - 7 rows structure per factory
 * - Indicators in column D (Produção, Substrato, MI, ME, Estoque Mercado)
 * - MI data from column E onwards
 * - Row 2 contains day numbers to identify valid columns
 */
export const parseProductionData = (
  worksheet: XLSX.WorkSheet,
  filters: {
    factory?: string;
    month: number;
    year: number;
  }
): DataProductionRecord[] => {
  console.log('🔍 Starting production data parsing with new structure logic');
  console.log('📋 Filters:', filters);

  const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
  const rows: any[][] = [];
  
  // Convert worksheet to 2D array
  for (let R = range.s.r; R <= range.e.r; R++) {
    const row: any[] = [];
    for (let C = range.s.c; C <= range.e.c; C++) {
      const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
      const cell = worksheet[cellAddress];
      row[C] = cell ? cell.v : undefined;
    }
    rows[R] = row;
  }

  console.log('📊 Total rows:', rows.length);
  console.log('📊 First few rows:', rows.slice(0, 5));

  // Find day numbers in row 2 (index 1) starting from column E (index 4)
  const dayColumns: { [day: number]: number } = {};
  const headerRow = rows[1]; // Row 2 (0-indexed)
  
  if (headerRow) {
    console.log('📅 Analyzing header row (row 2) for day numbers:', headerRow);
    
    for (let col = 4; col < headerRow.length; col++) { // Start from column E (index 4)
      const value = headerRow[col];
      console.log(`Column ${col} (${String.fromCharCode(65 + col)}): ${value}`);
      
      if (typeof value === 'number' && value >= 1 && value <= 31) {
        dayColumns[value] = col;
        console.log(`✅ Day ${value} found in column ${col}`);
      } else if (value !== undefined && value !== null && value !== '') {
        console.log(`❌ Non-day value found: ${value}, stopping day detection`);
        break; // Stop when we find non-day values (like "Total" or sum column)
      }
    }
  }
  
  console.log('📅 Day columns mapping:', dayColumns);

  // Find all factories and their MI data
  const records: DataProductionRecord[] = [];
  const factories: string[] = [];

  for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
    const row = rows[rowIndex];
    
    // Check if column B (index 1) contains a factory name
    const factoryCandidate = row[1]; // Column B
    
    if (factoryCandidate && typeof factoryCandidate === 'string' && factoryCandidate.trim() !== '') {
      const factoryName = factoryCandidate.trim();
      
      // Skip obvious header/non-factory rows
      if (factoryName.toLowerCase().includes('fábrica') || 
          factoryName.toLowerCase().includes('produção') ||
          factoryName.toLowerCase().includes('total') ||
          factoryName.match(/^\d+$/)) {
        continue;
      }
      
      console.log(`🏭 Potential factory found at row ${rowIndex + 1}: "${factoryName}"`);
      factories.push(factoryName);
      
      // Filter by selected factory if specified
      if (filters.factory && !factoryName.toLowerCase().includes(filters.factory.toLowerCase())) {
        console.log(`⏭️ Skipping factory "${factoryName}" (doesn't match filter "${filters.factory}")`);
        continue;
      }
      
      console.log(`✅ Processing factory: "${factoryName}"`);
      
      // Look for MI row in the next 7 rows (factory structure)
      for (let offset = 1; offset <= 7; offset++) {
        const checkRowIndex = rowIndex + offset;
        
        if (checkRowIndex >= rows.length) break;
        
        const checkRow = rows[checkRowIndex];
        const indicator = checkRow[3]; // Column D (index 3)
        
        console.log(`🔍 Row ${checkRowIndex + 1}, Column D: "${indicator}"`);
        
        if (indicator && typeof indicator === 'string' && indicator.trim().toUpperCase() === 'MI') {
          console.log(`📊 MI row found at row ${checkRowIndex + 1} for factory "${factoryName}"`);
          console.log('📊 MI row data:', checkRow);
          
          let totalProduction = 0;
          let validDays = 0;
          
          // Extract production values for each day
          for (const [day, columnIndex] of Object.entries(dayColumns)) {
            const dayNum = parseInt(day);
            const rawValue = checkRow[columnIndex];
            
            console.log(`📅 Day ${dayNum} (col ${columnIndex}) raw:`, rawValue);
            
            // Normalize the production value (treat empty as 0)
            const productionValue = normalizeNumericValue(rawValue);
            
            console.log(`📅 Day ${dayNum} normalized:`, productionValue);
            
            // Create date string (YYYY-MM-DD format)
            const dateStr = `${filters.year}-${filters.month.toString().padStart(2, '0')}-${dayNum.toString().padStart(2, '0')}`;
            
            // Check if date is valid for the given month
            const date = new Date(filters.year, filters.month - 1, dayNum);
            if (date.getMonth() !== filters.month - 1) {
              console.log(`❌ Invalid date for month ${filters.month}: day ${dayNum}`);
              continue; // Invalid date for this month
            }
            
            records.push({
              factory: factoryName,
              date: dateStr,
              production: productionValue
            });
            
            totalProduction += productionValue;
            validDays++;
            
            console.log(`✅ Added record: ${factoryName} - ${dateStr} - ${productionValue}`);
          }
          
          console.log(`🏭 Factory "${factoryName}" MI line summary:`);
          console.log(`- Valid days: ${validDays}`);
          console.log(`- Total production: ${totalProduction}`);
          
          // Check against the sum column if it exists
          const sumColumnIndex = Math.max(...Object.values(dayColumns)) + 1;
          const expectedTotal = checkRow[sumColumnIndex];
          if (expectedTotal !== undefined) {
            const normalizedExpected = normalizeNumericValue(expectedTotal);
            console.log(`- Expected total (from sum column): ${normalizedExpected}`);
            
            if (Math.abs(totalProduction - normalizedExpected) > 0.01) {
              console.warn(`⚠️ Total mismatch! Calculated: ${totalProduction}, Expected: ${normalizedExpected}`);
            } else {
              console.log(`✅ Total matches expected value!`);
            }
          }
          
          break; // Found MI row for this factory, move to next factory
        }
      }
    }
  }

  console.log(`🎯 Parsing completed. Found ${records.length} records from ${factories.length} factories`);
  console.log('🏭 Detected factories:', factories);

  return records;
};

/**
 * Extract unique factories from Excel file
 */
export const extractFactoriesFromExcel = (file: File): Promise<string[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        
        const productionSheet = findProductionSheet(workbook);
        if (!productionSheet) {
          console.log('Production sheet not found');
          resolve([]);
          return;
        }
        
        const range = XLSX.utils.decode_range(productionSheet['!ref'] || 'A1');
        const factories = new Set<string>();
        
        // Extract factory names
        for (let R = range.s.r; R <= range.e.r; R++) {
          const row: any[] = [];
          for (let C = range.s.c; C <= range.e.c; C++) {
            const cellAddress = XLSX.utils.encode_cell({ c: C, r: R });
          const cell = productionSheet[cellAddress];
            row.push(cell ? cell.v : null);
          }
          
          if (isFactoryRow(row)) {
            const factoryName = extractFactoryName(row);
            if (factoryName) {
              factories.add(factoryName);
              console.log('Extracted factory:', factoryName);
            }
          }
        }
        
        const factoryList = Array.from(factories).sort();
        console.log('All factories found:', factoryList);
        resolve(factoryList);
      } catch (error) {
        console.error('Error extracting factories:', error);
        reject(error);
      }
    };
    
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });
};

/**
 * Calculate maximum daily production volume from production data
 */
export const calculateMaxDailyVolume = (productionData: DataProductionRecord[]): number => {
  if (!productionData || productionData.length === 0) {
    return 0;
  }

  // Group by date and sum production for each day
  const dailyProduction = new Map<string, number>();
  
  productionData.forEach(record => {
    const current = dailyProduction.get(record.date) || 0;
    dailyProduction.set(record.date, current + record.production);
  });

  // Find the maximum daily production
  let maxVolume = 0;
  dailyProduction.forEach(volume => {
    if (volume > maxVolume) {
      maxVolume = volume;
    }
  });

  return maxVolume;
};

/**
 * Process Excel file and return production data only
 */
export const processExcelFile = (
  file: File,
  filters: ExcelFilters
): Promise<ExcelProcessingResult> => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        
        console.log('Processing Excel with filters:', filters);
        
        // Find required sheet
        const productionSheet = findProductionSheet(workbook);
        
        if (!productionSheet) {
          resolve({
            success: false,
            error: 'MISSING_REQUIRED_SHEETS',
            errorDetails: ['Planilha de Produção não encontrada']
          });
          return;
        }
        
        // Parse production data
        const productionRecords = parseProductionData(productionSheet, filters);
        
        if (productionRecords.length === 0) {
          resolve({
            success: false,
            error: 'NO_PRODUCTION_DATA',
            errorDetails: [`Nenhum dado de produção encontrado para a fábrica "${filters.factory}" no período selecionado`]
          });
          return;
        }
        
        // Calculate summary
        const uniqueDates = new Set(productionRecords.map(r => r.date));
        const dateArray = Array.from(uniqueDates).sort();
        const uniqueFactories = new Set(productionRecords.map(r => r.factory));
        
        // Calculate maximum daily volume
        const maxDailyVolume = calculateMaxDailyVolume(productionRecords);
        
        const productionResult = {
          productionData: productionRecords,
          summary: {
            totalDays: uniqueDates.size,
            totalRecords: productionRecords.length,
            dateRange: {
              start: dateArray[0] || '',
              end: dateArray[dateArray.length - 1] || ''
            },
            factories: Array.from(uniqueFactories),
            maxDailyVolume: maxDailyVolume
          }
        };
        
        console.log('Processing successful:', productionResult);
        
        resolve({
          success: true,
          data: productionResult
        });
        
      } catch (error) {
        console.error('Excel processing error:', error);
        resolve({
          success: false,
          error: 'FILE_PARSING_ERROR',
          errorDetails: [error instanceof Error ? error.message : 'Erro desconhecido ao processar arquivo']
        });
      }
    };
    
    reader.onerror = () => {
      resolve({
        success: false,
        error: 'FILE_PARSING_ERROR',
        errorDetails: ['Falha ao ler o arquivo']
      });
    };
    
    reader.readAsArrayBuffer(file);
  });
}; 